import { Alert, Button, Card, Flex } from 'antd';
import { PlayCircleOutlined, StepForwardOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type {
  PipelineRunMode,
  PipelineStepId,
  PipelineStepState,
} from '../../types';
import { isAnyPipelineStepRunning } from '../../hooks/pipeline-run-lock';
import { PipelineResultSection } from '../PipelineResultSection/PipelineResultSection';
import { PipelineStepOverview } from '../PipelineStepOverview/PipelineStepOverview';

type PipelineFlowCardProps = {
  runMode: PipelineRunMode;
  activeStepIndex: number;
  activeStepId: PipelineStepId;
  activeStep: PipelineStepState;
  steps: Record<PipelineStepId, PipelineStepState>;
  canRun: boolean;
  exampleInput: unknown;
  exampleOutput: unknown;
  onStepSelect: (index: number) => void;
  onRun: () => void;
  onContinue: () => void;
  onUpdateInput: (value: unknown) => void;
  onUpdateOutput: (value: unknown) => void;
  onCopyInput: () => void;
  onCopyOutput: () => void;
  onDownloadInput: () => void;
  onDownloadOutput: () => void;
  onCopyExampleInput: () => void;
  onCopyExampleOutput: () => void;
  onDownloadExampleInput: () => void;
  onDownloadExampleOutput: () => void;
  onStepInputFilesSelected: (files: File[]) => void;
  isUploadDisabled?: boolean;
};

export function PipelineFlowCard({
  runMode,
  activeStepIndex,
  activeStepId,
  activeStep,
  steps,
  canRun,
  exampleInput,
  exampleOutput,
  onStepSelect,
  onRun,
  onContinue,
  onUpdateInput,
  onUpdateOutput,
  onCopyInput,
  onCopyOutput,
  onDownloadInput,
  onDownloadOutput,
  onCopyExampleInput,
  onCopyExampleOutput,
  onDownloadExampleInput,
  onDownloadExampleOutput,
  onStepInputFilesSelected,
  isUploadDisabled = false,
}: PipelineFlowCardProps) {
  const { t } = useTranslation('pipelineDebug');
  const isBusy = isAnyPipelineStepRunning(steps);
  const showContinue =
    runMode === 'step-by-step' &&
    activeStep.status === 'completed' &&
    activeStepIndex < 3;

  return (
    <Card
      title={t('flowTitle')}
      className="pipeline-debug-card"
      styles={{ body: { padding: 16 } }}
    >
      <Flex vertical gap={16}>
        <PipelineStepOverview
          steps={steps}
          activeStepIndex={activeStepIndex}
          isSelectDisabled={isBusy}
          onStepSelect={onStepSelect}
        />

        <Flex gap={8} wrap align="center" className="pipeline-debug-actions">
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            disabled={!canRun || isBusy}
            loading={isBusy}
            onClick={onRun}
          >
            {runMode === 'continuous'
              ? t('actions.runPipeline')
              : t('actions.runStep')}
          </Button>
          {showContinue ? (
            <Button
              icon={<StepForwardOutlined />}
              disabled={isBusy}
              onClick={onContinue}
            >
              {t('actions.continue')}
            </Button>
          ) : null}
        </Flex>

        {activeStep.error ? (
          <Alert
            type="error"
            showIcon
            message={t('messages.stepFailed')}
            description={activeStep.error}
          />
        ) : null}

        <PipelineResultSection
          activeStepId={activeStepId}
          activeStep={activeStep}
          exampleInput={exampleInput}
          exampleOutput={exampleOutput}
          isEditingDisabled={isBusy}
          isUploadDisabled={isUploadDisabled || isBusy}
          onStepInputFilesSelected={onStepInputFilesSelected}
          onUpdateInput={onUpdateInput}
          onUpdateOutput={onUpdateOutput}
          onCopyInput={onCopyInput}
          onCopyOutput={onCopyOutput}
          onDownloadInput={onDownloadInput}
          onDownloadOutput={onDownloadOutput}
          onCopyExampleInput={onCopyExampleInput}
          onCopyExampleOutput={onCopyExampleOutput}
          onDownloadExampleInput={onDownloadExampleInput}
          onDownloadExampleOutput={onDownloadExampleOutput}
        />
      </Flex>
    </Card>
  );
}
